/**
 * Custom-domain host detection helpers for the SPA.
 *
 * `VITE_APP_HOST` is the platform's own canonical host (e.g. "app.yourerp.com").
 * When it is UNSET the whole custom-domain feature is dormant — every host is
 * treated as canonical, so local dev (localhost) and the default deployment work
 * exactly as before. To test custom domains locally, set VITE_APP_HOST and visit
 * e.g. http://acme.localhost:5173.
 */
export function canonicalHost(): string | null {
  return import.meta.env.VITE_APP_HOST ?? null;
}

/** True when the current host is the platform host (or detection is disabled). */
export function isCanonicalHost(): boolean {
  const h = canonicalHost();
  if (!h) return true;
  const cur = window.location.hostname.toLowerCase();
  const base = h.toLowerCase();
  return cur === base || cur === `www.${base}`;
}

/**
 * Paths that own their own tenant resolution / routing and must NOT be
 * overridden by host-based serving: platform admin, the slug-based storefront,
 * the customer portal, and public invoice links.
 *
 * `/d` and `/o` were missing, which is a live bug on any storefront-kind host:
 * the gate rewrites anything outside /s/ into the storefront tree, so a printed
 * invoice QR (/d/<token>) or an order link (/o/<token>) landed on
 * StorefrontNotFoundPage instead of the document it names.
 *
 * `/admin` is here so the dashboard renders immediately on a shared domain —
 * it is served under that prefix on duhokstationery.com, resolves its tenant
 * from the session rather than the host, and would otherwise sit behind a
 * full-screen spinner on every page load waiting for a site-context fetch it
 * does not need.
 */
export function pathIsReserved(): boolean {
  return /^\/(platform|s|portal|i|d|o|admin)(\/|$)/.test(window.location.pathname);
}
